/** Static Web Apps identity: /.auth/me tells the SPA who is signed in; the API learns it from x-ms-client-principal. */
import { useEffect, useState } from 'react';
import { call } from '../api/client.ts';

export interface ClientPrincipal { identityProvider: string; userId: string; userDetails: string; userRoles: string[]; claims?: { typ: string; val: string }[] }
export interface AuthState { loading: boolean; principal: ClientPrincipal | null; displayName: string; email: string; isOperator: boolean }

export const loginUrl = (redirect = location.pathname + location.search) => `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(redirect || '/')}`;
export const logoutUrl = '/.auth/logout?post_logout_redirect_uri=/acceso';

export async function fetchPrincipal(): Promise<ClientPrincipal | null> {
  try {
    const r = await fetch('/.auth/me', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!r.ok) return null;
    const j = (await r.json()) as { clientPrincipal?: ClientPrincipal | null };
    return j.clientPrincipal ?? null;
  } catch { return null; }
}

/** The `name` claim (sent once `profile` is in the login scope — staticwebapp.config.json), else given + family name, else the UPN. Mirrors the API's displayNameFrom. */
export function displayNameOf(p: ClientPrincipal | null): string {
  if (!p) return '';
  const claim = (t: string) => p.claims?.find((c) => c.typ === t || c.typ.endsWith('/' + t))?.val?.trim() || undefined;
  const full = [claim('given_name') ?? claim('givenname'), claim('family_name') ?? claim('surname')].filter(Boolean).join(' ');
  return claim('name') ?? (full || undefined) ?? p.userDetails;
}
/** The e-mail the API stores on app_user: the `emails`/`preferred_username`/`email` claim, else the UPN in userDetails. Mirrors principalFrom in functions.ts. */
export function emailOf(p: ClientPrincipal | null): string {
  if (!p) return '';
  const claim = (t: string) => p.claims?.find((c) => c.typ === t || c.typ.endsWith('/' + t))?.val?.trim() || undefined;
  return claim('emails') ?? claim('preferred_username') ?? claim('email') ?? p.userDetails;
}

/** The API's request principal carries no claims, so the API only knows the UPN; the SPA, which reads /.auth/me, tells it the name once per session (bound to the caller — PATCH /api/me). */
async function syncDisplayName(name: string, email: string): Promise<void> {
  if (!name || name === email) return;
  try { const me = await call('me.get'); if (me.displayName !== name) await call('me.patch', { body: { displayName: name } }); } catch { /* cosmetic: the next session tries again */ }
}
export function useAuth(): AuthState {
  const [state, set] = useState<AuthState>({ loading: true, principal: null, displayName: '', email: '', isOperator: false });
  useEffect(() => {
    let alive = true;
    fetchPrincipal().then((p) => { if (!alive) return; const displayName = displayNameOf(p); const email = emailOf(p); set({ loading: false, principal: p, displayName, email, isOperator: p?.userRoles.includes('factura-admin') ?? false }); if (p) void syncDisplayName(displayName, email); });
    return () => { alive = false; };
  }, []);
  return state;
}
